/* Motion.

   One observer, one pass, no library. GSAP and ScrollTrigger are what the
   reference site uses and they are the right answer for pinned, scrubbed
   sequences; for a reveal this is 40 lines and no 70 KB download, and a
   catalogue of 207 pages should not pay for a dependency it uses once.

   The attribute is written here rather than in the templates for two reasons.
   It keeps the markup of twelve sections untouched, and it means a reader
   whose JavaScript failed never meets a hidden element — the CSS only hides
   what this file has marked, and if this file did not run, nothing is marked.

   Order matters more than distance. Everything in one row leaves together but
   a beat apart, left to right, which is what reads as choreography rather than
   as a page that is slow to load. */
(function () {
  "use strict";

  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (!("IntersectionObserver" in window)) return;

  /* Blocks that rise. A section head, then whatever that section is made of.

     The hero is deliberately absent from this list.

     It used to be here as ".mps-heroin > *", which put the hero copy — and so
     the H1 inside it — at opacity 0 until this file had loaded, run, and
     finished a 720ms transition. The launch audit measured the result: the
     mobile largest-contentful-paint element is that H1, at 4.7 seconds, with
     the diagnostic pointing straight at it. The first sentence a buyer reads
     was the last thing on the page to appear.

     Nothing above the fold animates now. The choreography starts at the first
     block below the hero, which is where it was always doing its real work —
     a page settling into place as you scroll it, not a headline arriving late
     to its own page. */
  var RISE = [
    ".mps-head", ".mpc-head", ".mpu-head", ".mpr-head",
    ".mps-closer",
    ".mpc-tile", ".mpu-card", ".mps-card", ".mps-tile", ".mps-prop",
    ".mpx-card", ".mpd", ".mpr-sum", ".mpr-card", ".mps-slide .cap"
  ].join(",");

  /* Image trays that wipe open. */
  var WIPE = ".mpc-ph, .mpu-ph, .mps-shot, .mps-card .ph";

  var STEP = 70;    /* ms between neighbours in the same row */
  var CAP  = 5;     /* never stagger more than this many, or the last one crawls */

  function mark(el, attr, delay) {
    if (el.hasAttribute(attr) || el.classList.contains("in")) return;
    el.setAttribute(attr, "");
    if (delay) el.style.setProperty("--d", delay + "ms");
  }

  /* Siblings inside one grid share a start and separate by a beat. */
  function stagger(list) {
    var rows = new Map();
    list.forEach(function (el) {
      var p = el.parentNode || document.body;
      if (!rows.has(p)) rows.set(p, []);
      rows.get(p).push(el);
    });
    rows.forEach(function (group) {
      group.forEach(function (el, i) { mark(el, "data-rise", Math.min(i, CAP) * STEP); });
    });
  }

  function boot() {
    var risers = Array.prototype.slice.call(document.querySelectorAll(RISE));
    stagger(risers);
    Array.prototype.slice.call(document.querySelectorAll(WIPE))
      .forEach(function (el) { mark(el, "data-wipe", 90); });

    var watched = document.querySelectorAll("[data-rise],[data-wipe]");
    if (!watched.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add("in");
        io.unobserve(e.target);
      });
    }, {
      /* Fires a little before the edge, so a block is already settling by the
         time it is properly on screen rather than starting when it arrives. */
      rootMargin: "0px 0px -8% 0px",
      threshold: 0.04
    });

    Array.prototype.slice.call(watched).forEach(function (el) { io.observe(el); });

    /* The net.

       A reveal animation has exactly one catastrophic failure: something stays
       hidden and a buyer never learns it was there. It nearly happened on the
       first cut of this file — a clip-path gave the observed element an
       intersection ratio of zero, so the observer could never see the thing it
       was waiting for. Six seconds after load, anything still waiting is shown
       regardless. If the mechanism ever breaks again the page degrades to
       plain, which is the only acceptable way for it to break. */
    window.setTimeout(function () {
      Array.prototype.slice.call(watched).forEach(function (el) {
        if (!el.classList.contains("in")) {
          el.style.setProperty("--d", "0ms");
          el.classList.add("in");
          io.unobserve(el);
        }
      });
    }, 6000);

    /* Anything already on screen at load should not animate in — it was never
       scrolled to. Reveal it on the next frame, without the stagger. */
    window.requestAnimationFrame(function () {
      Array.prototype.slice.call(watched).forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.92) {
          el.style.setProperty("--d", "0ms");
          el.classList.add("in");
          io.unobserve(el);
        }
      });
    });
  }

  /* The bar. */
  function bar() {
    var on = false;
    function read() {
      var now = (window.pageYOffset || document.documentElement.scrollTop) > 8;
      if (now === on) return;
      on = now;
      document.documentElement.classList.toggle("mp-scrolled", on);
    }
    read();
    window.addEventListener("scroll", function () {
      window.requestAnimationFrame(read);
    }, { passive: true });
  }

  function go() { boot(); bar(); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", go);
  else go();
  document.addEventListener("shopify:section:load", boot);
})();
